import React from 'react';

import {
  RadioGroupOption,
  LegacyText,
  Tooltip,
  LegacyBox,
  ToggleButton,
  Popover,
  IconButton,
  LegendToggleIcon,
} from '@gogame/components';
import { withForm, FormField } from '@gogame/form';

import { MetricMixedTypes, BreakdownMixedTypes } from './MixedTypes';
import { SettingsBlock, SettingsBlocksWrapper } from './components';
import {
  CHART_VIEW_SETTINGS_BRUSH,
  CHART_VIEW_SETTINGS_LEGEND,
  CHART_VIEW_SETTINGS_PERCENTAGE,
  CHART_VIEW_SETTINGS_STACKED,
  CHART_VIEW_SETTINGS_TYPE,
  CHART_VIEW_SETTINGS_PIE_LABEL_TYPE,
  CHART_VIEW_SETTINGS_FULL_NAME,
  CHART_VIEW_SETTINGS_LABEL,
  CHART_METRIC_MIXED_TYPES,
  CHART_BREAKDOWN_METRIC_TYPES,
} from './constants';
import { CHART_ICONS } from './icons';

export const ViewSettingsForm = withForm({
  enableReinitialize: true,
})(({
  submitForm,
  dirty,
  withPie,
  withScatter = false,
  withStacked,
  withPercentage,
  withLabel,
  withLegendDetails,
  withMixedType,
  disabledPie,
  showBrushControl,
  values,
  metrics,
  colorGetter,
  breakdownMode,
}) => {
  const handleClose = () => {
    if (dirty) {
      submitForm();
    }
  };

  const lineChartMode = values[CHART_VIEW_SETTINGS_TYPE] === 'line';
  const pieChartMode = values[CHART_VIEW_SETTINGS_TYPE] === 'pie';
  const scatterChartMode = values[CHART_VIEW_SETTINGS_TYPE] === 'scatter';
  const mixedChartMode = values[CHART_VIEW_SETTINGS_TYPE] === 'mixed';
  const mixedTypes = breakdownMode
    ? values[CHART_BREAKDOWN_METRIC_TYPES] || []
    : values[CHART_METRIC_MIXED_TYPES] || [];
  const hasMixedTypes = mixedTypes?.length > 0;

  return (
    <Popover
      anchor={
        <IconButton icon={<LegendToggleIcon />} tooltip={'Chart tools'} />
      }
      onClose={handleClose}
      PaperProps={{
        style: {
          width: '260px',
        },
      }}
    >
      <SettingsBlocksWrapper>
        <SettingsBlock>
          <div style={{ padding: '0 0 0' }}>
            <FormField.ToggleButtonGroup
              label={'Type'}
              name={CHART_VIEW_SETTINGS_TYPE}
              size={'small'}
              padding={'0 0 16px'}
            >
              <ToggleButton
                icon={CHART_ICONS.line}
                value={'line'}
                tooltip={'Line'}
                data-cy={'lineButton'}
              />
              <ToggleButton
                icon={CHART_ICONS.bar}
                value={'bar'}
                tooltip={'Bar'}
                data-cy={'barButton'}
              />
              {withScatter ? (
                <ToggleButton
                  icon={CHART_ICONS.scatter}
                  value={'scatter'}
                  tooltip={'Dot'}
                  data-cy={'dotButton'}
                />
              ) : null}
              {withPie ? (
                <ToggleButton
                  icon={CHART_ICONS.pie}
                  value={'pie'}
                  disabled={disabledPie}
                  tooltip={'Pie'}
                  data-cy={'pieButton'}
                />
              ) : null}
              {withMixedType ? (
                <ToggleButton
                  value={'mixed'}
                  tooltip={'Mixed'}
                  disabled={!hasMixedTypes}
                >
                  <div
                    style={{
                      padding: '4px 0',
                      fontSize: '14px',
                      letterSpacing: '1.25px',
                    }}
                  >
                    mixed
                  </div>
                </ToggleButton>
              ) : null}
            </FormField.ToggleButtonGroup>

            {mixedChartMode ? (
              breakdownMode ? (
                <BreakdownMixedTypes
                  name={CHART_BREAKDOWN_METRIC_TYPES}
                  colorGetter={colorGetter}
                  padding={'0 8px 16px'}
                />
              ) : (
                <MetricMixedTypes
                  metrics={metrics}
                  name={CHART_METRIC_MIXED_TYPES}
                  colorGetter={colorGetter}
                  padding={'0 8px 16px'}
                />
              )
            ) : null}
            {(withStacked || withPercentage) && !pieChartMode ? (
              <div style={{ padding: '0 0 16px' }}>
                <LegacyBox style={{ padding: '0 8px 8px' }}>
                  <LegacyText variant={'caption'} intensity={200}>
                    Features
                  </LegacyText>
                </LegacyBox>
                {withStacked && !scatterChartMode ? (
                  <FormField.Checkbox
                    name={CHART_VIEW_SETTINGS_STACKED}
                    label={'Stacked'}
                    padding={'0'}
                  />
                ) : null}
                {withPercentage ? (
                  <FormField.Checkbox
                    name={CHART_VIEW_SETTINGS_PERCENTAGE}
                    label={'Percentage'}
                    padding={'0'}
                  />
                ) : null}
              </div>
            ) : null}
            {withPie && pieChartMode ? (
              <FormField.RadioGroup
                title={'Pie data type'}
                name={CHART_VIEW_SETTINGS_PIE_LABEL_TYPE}
                radioVertical={true}
                padding={'0 0 16px'}
              >
                <RadioGroupOption
                  label={'Value'}
                  value={'value'}
                  data-cy={'valueRadio'}
                />
                <RadioGroupOption
                  label={'Percent'}
                  value={'percent'}
                  data-cy={'percentRadio'}
                />
              </FormField.RadioGroup>
            ) : null}
          </div>
          <div>
            <LegacyBox>
              <LegacyText variant={'caption'} intensity={200}>
                Tools
              </LegacyText>
            </LegacyBox>
            {!showBrushControl ? (
              <Tooltip
                title={'Unable to use for periods less than 5 days'}
                wrapper
                arrow
                placement={'top'}
              >
                <FormField.Checkbox
                  name={CHART_VIEW_SETTINGS_BRUSH}
                  label={'Period slider'}
                  padding={'0'}
                  disabled={!showBrushControl}
                />
              </Tooltip>
            ) : (
              <FormField.Checkbox
                name={CHART_VIEW_SETTINGS_BRUSH}
                label={'Period slider'}
                padding={'0'}
                disabled={pieChartMode}
              />
            )}
            <FormField.Checkbox
              name={CHART_VIEW_SETTINGS_LEGEND}
              label={'Legend'}
              padding={'0'}
            />
            {mixedChartMode}
            {!withLabel ||
            pieChartMode ||
            lineChartMode ||
            mixedChartMode ? null : (
              <FormField.Checkbox
                name={CHART_VIEW_SETTINGS_LABEL}
                label={'Value labels'}
                padding={'0'}
              />
            )}
          </div>
          {withLegendDetails ? (
            <div style={{ padding: '16px 0 0' }}>
              <FormField.RadioGroup
                title={'Legend details'}
                name={CHART_VIEW_SETTINGS_FULL_NAME}
                radioVertical={true}
                padding={'0'}
              >
                <RadioGroupOption
                  label={'Metric name'}
                  value={false}
                  data-cy={'metricNameRadio'}
                />
                <RadioGroupOption
                  label={'Full row name'}
                  value={true}
                  data-cy={'fullRowNameRadio'}
                />
              </FormField.RadioGroup>
            </div>
          ) : null}
        </SettingsBlock>
      </SettingsBlocksWrapper>
    </Popover>
  );
});
